import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { Target, Plus, Trash2, Check, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { cn } from '../lib/utils';

export default function GoalsPage() {
  const { getAuthHeader, API } = useAuth();
  const { t, i18n } = useTranslation();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    target_date: '',
    category: 'performance'
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/goals`, { headers: getAuthHeader() });
      setGoals(response.data);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const createGoal = async (e) => {
    e.preventDefault();
    if (!newGoal.title || !newGoal.target_date) {
      toast.error(i18n.language === 'no' ? 'Fyll inn alle obligatoriske felt' : 'Please fill in all required fields');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/goals`,
        newGoal,
        { headers: getAuthHeader() }
      );
      setGoals([...goals, response.data]);
      setDialogOpen(false);
      setNewGoal({ title: '', description: '', target_date: '', category: 'performance' });
      toast.success(i18n.language === 'no' ? 'Mål opprettet!' : 'Goal created!');
    } catch (error) {
      toast.error(i18n.language === 'no' ? 'Kunne ikke opprette mål' : 'Failed to create goal');
    }
  };

  const updateProgress = async (goalId, progress) => {
    try {
      await axios.put(
        `${API}/goals/${goalId}`,
        { progress },
        { headers: getAuthHeader() }
      );
      setGoals(goals.map(g => g.id === goalId ? { ...g, progress } : g));
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const toggleComplete = async (goal) => {
    try {
      await axios.put(
        `${API}/goals/${goal.id}`,
        { completed: !goal.completed, progress: !goal.completed ? 100 : goal.progress },
        { headers: getAuthHeader() }
      );
      setGoals(goals.map(g => 
        g.id === goal.id ? { ...g, completed: !g.completed, progress: !g.completed ? 100 : g.progress } : g
      ));
      toast.success(goal.completed ? 'Goal reopened' : 'Goal completed! 🎉');
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  const deleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API}/goals/${goalId}`, { headers: getAuthHeader() });
      setGoals(goals.filter(g => g.id !== goalId));
      toast.success('Goal deleted');
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const categoryLabels = {
    performance: 'Performance',
    mental: 'Mental',
    physical: 'Physical',
    career: 'Career',
    personal: 'Personal'
  };

  const activeGoals = goals.filter(g => !g.completed);
  const completedGoals = goals.filter(g => g.completed);

  return (
    <Layout>
      <div className="space-y-8" data-testid="goals-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif font-bold text-2xl md:text-3xl text-foreground">Goals</h1>
            <p className="text-muted-foreground">Set targets, track progress, achieve greatness</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground rounded-full" data-testid="add-goal-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Goal
              </Button>
            </DialogTrigger>
            <DialogContent className="glass border-primary/20">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Create New Goal</DialogTitle>
              </DialogHeader>
              <form onSubmit={createGoal} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Goal Title</Label>
                  <Input
                    id="title"
                    value={newGoal.title}
                    onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                    placeholder="Break my personal record"
                    className="bg-input/50 border-transparent focus:border-primary"
                    data-testid="goal-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newGoal.description}
                    onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                    placeholder="What does achieving this goal look like?"
                    className="bg-input/50 border-transparent focus:border-primary resize-none"
                    data-testid="goal-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target_date">{i18n.language === 'no' ? 'Måldato' : 'Target Date'}</Label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      id="target_date"
                      type="date"
                      value={newGoal.target_date}
                      onChange={(e) => setNewGoal({ ...newGoal, target_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="pl-10 bg-input/50 border-transparent focus:border-primary"
                      data-testid="goal-date-picker"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={newGoal.category} onValueChange={(v) => setNewGoal({ ...newGoal, category: v })}>
                    <SelectTrigger className="bg-input/50 border-transparent" data-testid="goal-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="performance">Performance</SelectItem>
                      <SelectItem value="mental">Mental</SelectItem>
                      <SelectItem value="physical">Physical</SelectItem>
                      <SelectItem value="career">Career</SelectItem>
                      <SelectItem value="personal">Personal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-primary text-primary-foreground rounded-full" data-testid="create-goal-btn">
                  Create Goal
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Active Goals */}
        <div>
          <h2 className="font-serif text-xl text-foreground mb-4">Active Goals ({activeGoals.length})</h2>
          {activeGoals.length === 0 ? (
            <Card className="bg-card/50 border-white/5">
              <CardContent className="p-8 text-center">
                <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No active goals. Set your first goal to begin tracking.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              <AnimatePresence>
                {activeGoals.map((goal, index) => (
                  <motion.div
                    key={goal.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="bg-card/50 border-white/5 hover:border-primary/30 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-mono uppercase text-primary/80 tracking-wider">
                                {categoryLabels[goal.category]}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                • Due {format(new Date(goal.target_date), 'MMM d, yyyy')}
                              </span>
                            </div>
                            <h3 className="font-serif font-semibold text-lg text-foreground">{goal.title}</h3>
                            {goal.description && (
                              <p className="text-sm text-muted-foreground mt-1">{goal.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleComplete(goal)}
                              className="hover:bg-secondary/20 hover:text-secondary"
                              data-testid={`complete-goal-${goal.id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteGoal(goal.id)}
                              className="hover:bg-destructive/20 hover:text-destructive"
                              data-testid={`delete-goal-${goal.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Progress</span>
                            <span className="text-foreground font-semibold">{goal.progress}%</span>
                          </div>
                          <Progress value={goal.progress} className="h-2" />
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={goal.progress}
                            onChange={(e) => updateProgress(goal.id, parseInt(e.target.value))}
                            className="w-full accent-primary cursor-pointer"
                            data-testid={`progress-slider-${goal.id}`}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Completed Goals */}
        {completedGoals.length > 0 && (
          <div>
            <h2 className="font-serif text-xl text-foreground mb-4">Completed ({completedGoals.length})</h2>
            <div className="grid gap-4">
              {completedGoals.map((goal) => (
                <Card key={goal.id} className="bg-card/30 border-secondary/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-secondary/20">
                          <Check className="h-4 w-4 text-secondary" />
                        </div>
                        <div>
                          <h3 className="font-serif font-semibold text-foreground line-through opacity-75">{goal.title}</h3>
                          <p className="text-xs text-muted-foreground">{categoryLabels[goal.category]}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleComplete(goal)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          Reopen
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteGoal(goal.id)}
                          className="hover:bg-destructive/20 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
